import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { DataMgmtCatalogPagination } from 'src/components/configuration/dataMgmtCatalogUi';

export interface DrillDownColumn {
  key: string;
  label: string;
}

export interface FetchPageResult {
  rows: Record<string, any>[];
  totalCount: number;
  totalPages: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  loading?: boolean;
  columns: DrillDownColumn[];
  rows: Record<string, any>[];
  onRowClick?: (row: Record<string, any>) => void;
  /** Rows per page for the built-in client-side pagination. Default 15. */
  pageSize?: number;
  /**
   * When provided, the modal switches to server-driven mode: `rows` is
   * ignored and every page/search change re-fetches from the server instead
   * of paginating/filtering the full in-memory set. Use for feeds too large
   * to load in one shot (e.g. the 200+ row "View All" drill-downs).
   */
  fetchPage?: (args: { page: number; pageSize: number; search: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => Promise<FetchPageResult>;
}

/** Generic drill-down details modal — shared by custom charts, dynamic charts,
 * and full-list "View All" drill-downs off dashboard tables. */
export default function DrillDownModal({ isOpen, onClose, title, subtitle, loading, columns, rows, onRowClick, pageSize = 15, fetchPage }: Props) {
  const serverMode = !!fetchPage;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  // User-adjustable via the "Rows per page" select below — `pageSize` (the
  // prop) is only the initial default.
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  // Client mode sorts the full in-memory set locally (below). Server mode
  // instead passes sortKey/sortDir to fetchPage and lets the backend's
  // ORDER BY do it, so sorting stays correct across every page, not just
  // whatever's currently loaded.
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (key: string) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); } else { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
    setPage(1);
  };

  const [serverRows, setServerRows] = useState<Record<string, any>[]>([]);
  const [serverTotalCount, setServerTotalCount] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [serverLoading, setServerLoading] = useState(false);

  // Reset everything when the modal opens/closes so stale search/page state
  // from a previous open doesn't leak into the next one.
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setDebouncedSearch('');
      setPage(1);
      setSortKey(null);
      setSortDir('asc');
      setRowsPerPage(pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounce the search box so server mode doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  useEffect(() => {
    if (!serverMode || !isOpen) return;
    let cancelled = false;
    setServerLoading(true);
    fetchPage!({ page, pageSize: rowsPerPage, search: debouncedSearch, sortBy: sortKey || undefined, sortOrder: sortKey ? sortDir : undefined })
      .then(result => {
        if (cancelled) return;
        setServerRows(result.rows);
        setServerTotalCount(result.totalCount);
        setServerTotalPages(Math.max(1, result.totalPages));
      })
      .catch(() => {
        if (cancelled) return;
        setServerRows([]);
        setServerTotalCount(0);
        setServerTotalPages(1);
      })
      .finally(() => { if (!cancelled) setServerLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMode, isOpen, page, rowsPerPage, debouncedSearch, sortKey, sortDir]);

  const filtered = useMemo(() => {
    if (serverMode) return serverRows;
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => columns.some(c => String(r[c.key] ?? '').toLowerCase().includes(q)));
  }, [serverMode, serverRows, rows, search, columns]);

  // Reset to page 1 whenever the search term or the underlying row set changes
  // (e.g. re-opening the modal with fresh data) — otherwise a filter that
  // shrinks the result set can leave the view stuck past the last page.
  // (Server mode manages its own page resets above, keyed on debouncedSearch.)
  useEffect(() => { if (!serverMode) setPage(1); }, [serverMode, search, rows]);

  const sorted = useMemo(() => {
    // Server mode already comes back ordered by the backend's ORDER BY —
    // re-sorting here would just undo that (or double-sort) for no benefit.
    if (serverMode || !sortKey) return filtered;
    const withKey = filtered.map((row, idx) => ({ row, idx }));
    withKey.sort((a, b) => {
      const av = a.row[sortKey];
      const bv = b.row[sortKey];
      const aEmpty = av === null || av === undefined || av === '';
      const bEmpty = bv === null || bv === undefined || bv === '';
      if (aEmpty && bEmpty) return a.idx - b.idx;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      const an = Number(av);
      const bn = Number(bv);
      let cmp: number;
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        cmp = an - bn;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return cmp !== 0 ? cmp : a.idx - b.idx;
    });
    const ordered = withKey.map(w => w.row);
    return sortDir === 'desc' ? ordered.reverse() : ordered;
  }, [serverMode, filtered, sortKey, sortDir]);

  const totalPages = serverMode ? serverTotalPages : Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => (serverMode ? sorted : sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)),
    [serverMode, sorted, currentPage, rowsPerPage],
  );

  // Changing the page size mid-view resets to page 1 — the current page
  // number otherwise refers to a different slice of rows than before.
  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPage(1);
  };

  // (The jump-to-page box for long tables now lives in DataMgmtCatalogPagination.)

  const isLoading = loading || (serverMode && serverLoading);
  const totalRecordCount = serverMode ? serverTotalCount : rows.length;
  const filteredRecordCount = serverMode ? serverTotalCount : filtered.length;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" style={{ zIndex: 100 }}>
      <ModalHeader toggle={onClose} style={{ borderBottom: '2px solid #5a9b82' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>📋 {title}</div>
          {subtitle && <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 400, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', padding: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14 }}>🔍</span>
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '7px 12px 7px 32px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', color: '#374151' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16, lineHeight: 1 }}>×</button>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
            {filteredRecordCount} of {totalRecordCount} record{totalRecordCount !== 1 ? 's' : ''}
          </div>
        </div>
        {isLoading ? (
          <div className="text-center py-5" style={{ color: '#9CA3AF', fontSize: 13 }}>
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Loading details...
          </div>
        ) : paged.length === 0 ? (
          <div className="text-center py-5" style={{ color: '#9CA3AF', fontSize: 13 }}>No records found</div>
        ) : (
          <table className="table table-sm table-hover mb-0">
            <thead style={{ backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ fontSize: 12, fontWeight: 600, color: '#374151', padding: '10px 16px', borderBottom: '1px solid #E5E7EB' }}>#</th>
                {columns.map(c => (
                  <th key={c.key} style={{ fontSize: 12, fontWeight: 600, color: '#374151', padding: '10px 16px', borderBottom: '1px solid #E5E7EB' }}>
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none',
                        border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer',
                      }}
                    >
                      {c.label}
                      {sortKey === c.key
                        ? (sortDir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />)
                        : <ArrowUpDown size={13} style={{ opacity: 0.35 }} />}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((row, idx) => (
                <tr key={idx} style={{ cursor: onRowClick ? 'pointer' : 'default' }} onClick={() => onRowClick?.(row)}>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: '#9CA3AF', verticalAlign: 'middle' }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  {columns.map(c => (
                    <td key={c.key} style={{ padding: '10px 16px', fontSize: 13, color: '#374151', verticalAlign: 'middle' }}>
                      {row[c.key] !== null && row[c.key] !== undefined && row[c.key] !== '' ? String(row[c.key]) : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ModalBody>
      <ModalFooter style={{ borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        {!isLoading && filteredRecordCount > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280' }}>
              Rows per page
              <select
                value={rowsPerPage}
                onChange={e => handleRowsPerPageChange(Number(e.target.value))}
                style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, color: '#374151', background: '#fff' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </label>
            <DataMgmtCatalogPagination
              page={currentPage}
              totalPages={totalPages}
              totalFiltered={filteredRecordCount}
              busy={isLoading}
              onPageChange={setPage}
            />
          </div>
        ) : <span />}
        <Button color="secondary" size="sm" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
